/**
 * Six Guys+ RP Glossary System
 *
 * Contains all RP-specific terminology, supernatural lore, and world-building terms
 * extracted from the actual Discord RP history. Used to give AI characters accurate
 * knowledge of the RP universe when generating responses.
 */

// ============================================================
// FULL GLOSSARY — every major term from the RP
// ============================================================

const GLOSSARY = {

  // ── SIREN TERMS ──────────────────────────────────────────
  tailbond: {
    term: 'Tailbond',
    definition: 'A siren soul bond created when siren tails touch. Equivalent to marriage in siren culture. Creates a permanent mark on the tail. Considered unbreakable — everyone in siren society respects tailbonds. Multiple tailbonds are possible but extremely rare.',
    species: ['siren'],
    tags: ['bond', 'romance', 'siren culture'],
  },
  landbond: {
    term: 'Landbond',
    definition: 'A siren term for a soulmate connection formed on land (outside ocean form). Creates a telepathic link between bonded partners — they can share thoughts. Multiple landbonds are possible. Considered the most coveted, treasured, and sought-after connection in siren society.',
    species: ['siren'],
    tags: ['bond', 'romance', 'siren culture', 'telepathy'],
  },
  compulsion: {
    term: 'Compulsion',
    definition: 'A siren-vampire hybrid ability that allows the user to mentally control almost anyone, minus deities. Extremely powerful and feared.',
    species: ['siren', 'vampire', 'hybrid'],
    tags: ['power', 'ability'],
  },

  // ── FAIRY TERMS ──────────────────────────────────────────
  fairyBond: {
    term: 'Fairy Bond',
    definition: 'Created by touching fairy wings. Permanent and unbreakable. Makes the bonded partners fall in love over time whether they want to or not. Fairy wings are erogenous zones — touching them triggers the bonding process. Fairies are not fertile until they are bonded.',
    species: ['fairy'],
    tags: ['bond', 'romance', 'fairy culture'],
  },
  persuasionMagic: {
    term: 'Persuasion Magic',
    definition: 'A royal fairy ability. To activate the magic and order people, the fairy must have conviction — they have to believe what they say and want it enough to make it reality. Tied to royal bloodlines.',
    species: ['fairy'],
    tags: ['power', 'ability', 'royalty'],
  },
  fairyRealm: {
    term: 'Fairy Realm',
    definition: 'A separate plane of existence from the mortal realm, connected by portals. Fairies were sent to the mortal realm for protection. The fairy realm has its own leaders and governing structure, similar to how every country in the mortal realm has leaders.',
    species: ['fairy'],
    tags: ['world', 'location'],
  },
  mortalRealm: {
    term: 'Mortal Realm',
    definition: 'The human world / Earth plane. Connected to the fairy realm by portals. Where most of the RP action takes place.',
    species: ['all'],
    tags: ['world', 'location'],
  },

  // ── WEREWOLF TERMS ───────────────────────────────────────
  matingBond: {
    term: 'Mating Bond / Mating Mark',
    definition: 'A werewolf-specific bond created by biting — canines into the shoulder. Sacred in pack culture. Creates a strong, possessive emotional connection between mates. Mating bonds do not last after death. Can potentially be broken by Ralidanti (rumored).',
    species: ['werewolf'],
    tags: ['bond', 'romance', 'pack culture'],
  },
  alphaOrder: {
    term: 'Alpha Order / Alpha Command',
    definition: 'A command given by an alpha wolf that forces other wolves into physical submission. Their body obeys whether they want it to or not. A display of dominance and authority in pack hierarchy.',
    species: ['werewolf'],
    tags: ['power', 'ability', 'pack culture'],
  },
  luna: {
    term: 'Luna',
    definition: 'The title given to an alpha\'s mate in a werewolf pack. A position of honor and leadership alongside the alpha.',
    species: ['werewolf'],
    tags: ['title', 'pack culture'],
  },
  packLands: {
    term: 'Pack Lands',
    definition: 'Werewolf territory separated from human areas by high fences. The pack lives and operates within these boundaries. Pack members can share thoughts telepathically via the pack link when in wolf form.',
    species: ['werewolf'],
    tags: ['location', 'pack culture'],
  },
  packLink: {
    term: 'Pack Link',
    definition: 'A telepathic connection shared between pack members when in wolf form. Allows wolves to communicate thoughts with each other during shifts.',
    species: ['werewolf'],
    tags: ['ability', 'pack culture', 'telepathy'],
  },
  shifting: {
    term: 'Shifting',
    definition: 'The werewolf transformation between human and wolf forms. A painful physical process. Wolves can share thoughts via the pack link while shifted.',
    species: ['werewolf'],
    tags: ['ability', 'transformation'],
  },
  rogue: {
    term: 'Rogue',
    definition: 'A wolf without a pack. Rogue wolves are considered dangerous outsiders in pack society.',
    species: ['werewolf'],
    tags: ['status', 'pack culture'],
  },
  heat: {
    term: 'Heat',
    definition: 'A werewolf reproductive cycle. Some wolves go into heat more frequently than average. A biological drive tied to mating and reproduction.',
    species: ['werewolf'],
    tags: ['biology', 'pack culture'],
  },
  wolfsbane: {
    term: 'Wolfsbane',
    definition: 'A poison and weakness for werewolves. Can weaken wolf abilities and cause harm. Used as a weapon against wolves.',
    species: ['werewolf'],
    tags: ['weakness', 'substance'],
  },
  cliffsEdge: {
    term: "Cliff's Edge",
    definition: 'The small town near the werewolf pack lands, located in the Tennessee area. A recurring setting in werewolf-centric routes.',
    species: ['werewolf'],
    tags: ['location'],
  },

  // ── VAMPIRE TERMS ────────────────────────────────────────
  vampireBond: {
    term: 'Vampire Bond / Vampire Marriage',
    definition: 'A traditional vampiric marriage involving a blood ritual — cutting and sharing blood between partners. Creates an emotional connection where bonded partners can feel each other\'s emotions. Can be performed through trickery (as Junta did to Madison).',
    species: ['vampire'],
    tags: ['bond', 'romance', 'vampire culture'],
  },
  immortuos: {
    term: 'Immortuos / Immortuous',
    definition: 'A vampire elder organization or council. The governing body of vampire society. Members are all roughly equal in strength, which is the only reason the organization holds together. Handles supernatural affairs and investigations.',
    species: ['vampire'],
    tags: ['organization', 'vampire culture', 'leadership'],
  },
  theElders: {
    term: 'The Elders',
    definition: 'The vampire leadership council, including figures like Val, James, Junta, Sebastian, and Tim. They govern vampire society and are supposed to put vampire society first above personal interests.',
    species: ['vampire'],
    tags: ['organization', 'vampire culture', 'leadership'],
  },

  // ── CONDUIT / DEITY TERMS ────────────────────────────────
  conduit: {
    term: 'Conduit',
    definition: 'Beings who pull energy from natural sources and emotion. A conduit can ONLY love the person their energy is drawn to — no one else, ever. When a conduit gets pulled towards someone, that person is the only one they can love. Twin conduits drawn to the same person is unprecedented. The Conduit Council governs them and can debate killing those involved in dangerous prophecies.',
    species: ['conduit', 'deity'],
    tags: ['species', 'power', 'romance'],
  },
  conduitCouncil: {
    term: 'Conduit Council',
    definition: 'The governing body for conduits. Has the authority to debate extreme measures — including killing — when dangerous prophecies involving conduits arise.',
    species: ['conduit', 'deity'],
    tags: ['organization', 'leadership'],
  },
  prophecy: {
    term: 'Prophecy',
    definition: 'A major plot device in the RP universe. Prophecies involving conduits ALWAYS play out to the end — they cannot be stopped or avoided once set in motion.',
    species: ['all'],
    tags: ['lore', 'plot'],
  },

  // ── DANGEROUS BEINGS ─────────────────────────────────────
  ralidanti: {
    term: 'Ralidanti',
    definition: 'Dangerous supernatural beings that come out at night. Can potentially break mating bonds. Associated with mass killing. Some Ralidanti are ex-soldiers from the inquisitions — souls who truly could not be tortured and were transformed. Used as threats and plot devices throughout the RP.',
    species: ['ralidanti'],
    tags: ['species', 'danger', 'lore'],
  },

  // ── GENERAL SUPERNATURAL / WORLD TERMS ───────────────────
  theAbyss: {
    term: 'The Abyss / The Void',
    definition: 'An afterlife dimension where souls go after the second death. A dark, final destination beyond normal death.',
    species: ['all'],
    tags: ['world', 'afterlife', 'lore'],
  },
  secondDeath: {
    term: 'Second Death',
    definition: 'The death of an already-dead supernatural being. Considered far worse than the first death. Sends the soul to the Abyss/Void.',
    species: ['all'],
    tags: ['lore', 'death'],
  },
  reincarnation: {
    term: 'Reincarnation',
    definition: 'Characters can be reborn with different lives but the same soul. The reincarnated person may not have memories of their past life but carries the same soul essence.',
    species: ['all'],
    tags: ['lore', 'soul'],
  },
  soulmate: {
    term: 'Soulmate / Fated',
    definition: 'A destined connection between two people, different from species-specific bonds. Can be fated by deities like Aphrodite. Bonds (tailbonds, landbonds, etc.) can sometimes be followed to find one\'s soulmate.',
    species: ['all'],
    tags: ['romance', 'destiny', 'lore'],
  },
};


// ============================================================
// CHARACTER → SPECIES / KNOWLEDGE MAP
// Determines which glossary terms each character would know
// ============================================================

const CHARACTER_SPECIES = {
  // Sirens
  'val': ['siren', 'vampire', 'hybrid', 'elder'],
  'james': ['siren', 'vampire', 'hybrid', 'elder'],
  'sebastian': ['siren', 'vampire', 'elder'],
  'kya': ['siren'],
  'naia': ['siren'],

  // Vampires
  'junta': ['vampire', 'elder'],
  'tim': ['vampire', 'elder'],
  'madison': ['vampire'],
  'aaron': ['vampire'],

  // Werewolves
  'cruz': ['werewolf', 'alpha'],
  'micah': ['werewolf'],
  'eva': ['werewolf'],
  'lyra': ['werewolf'],

  // Fairies
  'cyrus': ['fairy', 'royalty'],
  'juliette': ['fairy'],
  'malakai': ['fairy'],

  // Conduits / Deities
  'damian': ['conduit', 'deity'],
  'reggie': ['conduit', 'deity'],

  // Humans / Other (know general supernatural terms if exposed)
  'nicole': ['human', 'aware'],
  'haley': ['human', 'aware'],
};

// Species that have knowledge of ALL supernatural terms (elders, ancients, aware humans)
const BROAD_KNOWLEDGE_TAGS = ['elder', 'aware', 'deity', 'hybrid'];


// ============================================================
// GLOSSARY RETRIEVAL FUNCTIONS
// ============================================================

/**
 * Returns glossary terms relevant to a specific character based on their
 * species, role, and what they would realistically know in-universe.
 *
 * @param {string} characterName - The character name (case-insensitive)
 * @returns {Array<{term: string, definition: string}>} Relevant glossary entries
 */
export function getCharacterGlossary(characterName) {
  if (!characterName) return getAllGlossaryTerms();

  const normalized = characterName.toLowerCase().trim();
  const speciesTags = CHARACTER_SPECIES[normalized];

  // If character isn't mapped, return everything (safe fallback)
  if (!speciesTags) return getAllGlossaryTerms();

  // Check if character has broad knowledge (elder, deity, aware, hybrid)
  const hasBroadKnowledge = speciesTags.some(tag => BROAD_KNOWLEDGE_TAGS.includes(tag));
  if (hasBroadKnowledge) return getAllGlossaryTerms();

  // Otherwise, filter to terms relevant to this character's species
  const relevant = [];
  for (const key of Object.keys(GLOSSARY)) {
    const entry = GLOSSARY[key];
    const isGeneralTerm = entry.species.includes('all');
    const isSpeciesMatch = entry.species.some(sp => speciesTags.includes(sp));

    if (isGeneralTerm || isSpeciesMatch) {
      relevant.push({ term: entry.term, definition: entry.definition });
    }
  }

  return relevant;
}

/**
 * Returns ALL glossary terms (used for characters with broad knowledge
 * or as a fallback when character isn't mapped).
 *
 * @returns {Array<{term: string, definition: string}>}
 */
export function getAllGlossaryTerms() {
  return Object.values(GLOSSARY).map(entry => ({
    term: entry.term,
    definition: entry.definition,
  }));
}

/**
 * Formats glossary terms into a string suitable for injection into AI prompts.
 *
 * @param {string|string[]} characterNameOrNames - One name (back-compat) or
 *   an array of names. When an array is passed, terms are unioned across all
 *   characters in the scene so any character in a multi-char RP scene gets
 *   vocab from every other character present (e.g. Cruz scenes pick up
 *   siren glossary if Kya is there).
 * @returns {string} Formatted glossary section for the AI prompt, or empty string if no terms
 */
export function getGlossaryPromptSection(characterNameOrNames) {
  const names = Array.isArray(characterNameOrNames)
    ? characterNameOrNames.filter(Boolean)
    : characterNameOrNames ? [characterNameOrNames] : [];
  if (names.length === 0) return '';

  // Union by term name — multiple chars can share a term; first definition wins.
  const seen = new Map();
  for (const n of names) {
    const terms = getCharacterGlossary(n) || [];
    for (const t of terms) {
      if (!seen.has(t.term)) seen.set(t.term, t);
    }
  }
  if (seen.size === 0) return '';

  const termLines = Array.from(seen.values()).map(t => `• ${t.term}: ${t.definition}`).join('\n');

  return `\nRP WORLD GLOSSARY — USE THESE TERMS CORRECTLY:
The following terms are specific to this RP universe. Use them naturally and accurately when they come up. Do NOT define or explain them to the user — the characters already know what these mean.

${termLines}
`;
}
